import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Globe } from "lucide-react";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { Button } from "@/components/ui/button";
import { useGenieDocuments } from "@/hooks/genie-documents/useGenieDocuments";
import { GENIE_DOCUMENT_FORMATS } from "@/types/genie-documents/genie-document";
import { Agent } from "@/types/agent/agent";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface DocumentsGenerationCardProps {
  agent: Agent;
  brand?: string;
}

/**
 * Branded documents, from inside the Genie.
 *
 * A summary and a way through, not a second builder. The documents are made
 * from the knowledge base and can only be published to this Genie's page, so
 * the whole surface lives at `/genie-hub/knowledge-base/:genieId/documents` —
 * this card reports what is there and sends you to it.
 */
export const DocumentsGenerationCard = ({ agent, brand = "#4E9CFF" }: DocumentsGenerationCardProps) => {
  const navigate = useNavigate();
  const { documents } = useGenieDocuments(agent.id);

  const documentsPath = `/genie-hub/knowledge-base/${agent.id}/documents`;
  const publishedCount = documents.filter((entry) => entry.isPublished).length;
  const availableFormats = GENIE_DOCUMENT_FORMATS.filter((format) => format.status === "available");

  return (
    <OverviewSectionCard
      title="Branded Documents"
      subtitle="Turn this genie's knowledge into a co-branded guide you can share, print or publish"
      genieIcon="document"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
    >
      <div className="space-y-4">
        {documents.length > 0 ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                {documents.length} document{documents.length === 1 ? "" : "s"}
              </span>
              {publishedCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#EBF5FF] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#2563EB]">
                  <Globe className="h-3 w-3" />
                  {publishedCount} on your page
                </span>
              )}
            </div>
            <ul className="space-y-1.5">
              {documents.slice(0, 3).map((entry) => (
                <li key={entry.row.id} className="truncate text-[13px] text-gray-700">
                  {entry.stored.title}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-3">
            {availableFormats.slice(0, 3).map((format) => (
              <div key={format.key} className="rounded-xl border border-gray-200 bg-white p-4">
                <GenieIcon name={format.genieIcon} size={28} className="mb-2 h-7 w-7" />
                <p className="text-sm font-semibold text-gray-900">{format.label}</p>
                <p className="mt-1 text-xs leading-snug text-gray-500">{format.description}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            onClick={() => navigate(documentsPath)}
            className="gap-1.5 border-0 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white hover:opacity-90"
          >
            {documents.length > 0 ? "Manage documents" : "Create a document"}
            <ArrowUpRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </OverviewSectionCard>
  );
};
